// src/pages/videos/VideoListPage.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Placeholder from "react-bootstrap/Placeholder";
import API from "../../../api";
import { useAuthModal } from "../../Auth/useAuthModal";
import { useAuth } from "../../../AuthUser";
import VideoCard from "../components/VideoCard";
import CategoryPillsRail from "../components/CategoryPillsRail";
import "../styles/video-list.css";

const BATCH_SIZE = 24;
const HISTORY_KEY = "videoHistoryIndex";
const HISTORY_CATEGORY_ID = "history";
const CACHE_KEY = "videoPageCache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

const AUTH_SCROLL_KEY = "AUTH_SCROLL_SHOWN";
const resetAuthModalFlag = () => { try { sessionStorage.removeItem(AUTH_SCROLL_KEY); } catch {} };

const SORT_LABELS = {
  popular: "Popular",
  newest: "Newest",
  oldest: "Oldest",
  title_az: "Title A–Z",
  title_za: "Title Z–A",
};

const SORTERS = {
  popular: (a, b) => {
    const ao = Number.isFinite(a?.orderBy) ? a.orderBy : (Number.isFinite(a?.Category?.orderBy) ? a.Category.orderBy : -9999);
    const bo = Number.isFinite(b?.orderBy) ? b.orderBy : (Number.isFinite(b?.Category?.orderBy) ? b.Category.orderBy : -9999);
    const an = ao === 0 ? -Infinity : ao;
    const bn = bo === 0 ? -Infinity : bo;
    return (bn - an) || (b.id - a.id);
  },
  newest: (a, b) => new Date(b.releaseOnDate || b.created_at) - new Date(a.releaseOnDate || a.created_at),
  oldest: (a, b) => new Date(a.releaseOnDate || a.created_at) - new Date(b.releaseOnDate || b.created_at),
  title_az: (a, b) => (a.title || "").localeCompare(b.title || ""),
  title_za: (a, b) => (b.title || "").localeCompare(a.title || ""),
};

function readHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    return arr.filter(e => {
      const key = e?.slug || e?.id;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch { return []; }
}

// MOBILE SORT DROPDOWN
function InlineSortDropdown({ value, onChange, className = "sort-inline d-lg-none" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const label = SORT_LABELS[value] || "Popular";

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (e.key === "Escape") setOpen(false);
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onDoc);
    };
  }, []);

  return (
      <div className={className} ref={ref}>
        <button type="button" className="sort-inline-toggle" onClick={() => setOpen(o => !o)}>
          {label}
        </button>
        <ul className="sort-menu" hidden={!open}>
          {Object.entries(SORT_LABELS).map(([key, text]) => (
              <li key={key}>
                <button className={`sort-item ${value === key ? "active" : ""}`} onClick={() => { onChange(key); setOpen(false); }}>
                  {text}
                </button>
              </li>
          ))}
        </ul>
      </div>
  );
}

// ✅ NEW: MOBILE CATEGORY DROPDOWN (ellipsis + left-aligned selected label)
function InlineCategoryDropdown({ categories = [], value, onChange, className = "sort-inline d-lg-none" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const label = useMemo(() => {
    if (!value) return "All Categories";
    const match = categories.find(c => String(c.id) === String(value));
    return match ? match.title : "All Categories";
  }, [value, categories]);

  const items = useMemo(() => [{ id: "", title: "All" }, ...categories], [categories]);

  useEffect(() => {
    const onClickAway = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  return (
      <div className={className} ref={ref}>
        <button
            type="button"
            className="sort-inline-toggle text-start"
            onClick={() => setOpen(o => !o)}
            //style={{ width: 200 }}  // keeps button narrow on small screens
        >
          {/* Truncated, left-aligned label with ellipsis */}
          <span className="d-inline-block text-truncate" style={{ maxWidth: 160 }}>
          {label}
        </span>
        </button>
        <ul className="sort-menu" hidden={!open}>
          {items.map(c => (
              <li key={String(c.id)}>
                <button
                    className={`sort-item ${String(value) === String(c.id) ? "active" : ""}`}
                    onClick={() => { onChange(c.id); setOpen(false); }}
                    title={c.title}
                >
                  {c.title}
                </button>
              </li>
          ))}
        </ul>
      </div>
  );
}

export default function VideoListPage() {
  const handleSortChange = (next) => {
    if (!auth?.user && (next === "newest" || next === "popular")) {
      openAuthModal({ mode: "signin", closable: false });
      return;
    }
    writeParam("sort", next);
  };

  const handleCategoryChange = (id) => {
    resetAuthModalFlag();
    if (!auth?.user && String(id) === String(HISTORY_CATEGORY_ID)) {
      openAuthModal({ mode: "signin", closable: false });
      return;
    }
    writeParam("category", id || "");
  };

  const { openAuthModal } = useAuthModal();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { if (auth?.user) { resetAuthModalFlag(); } }, [auth?.user]);


  const qParam = searchParams.get("q") || "";
  const categoryParam = searchParams.get("category") || "";
  const urlSort = searchParams.get("sort");
  const derivedDefaultSort = (!categoryParam || categoryParam === HISTORY_CATEGORY_ID) ? "newest" : "popular";
  const sortParam = urlSort || derivedDefaultSort;

  const [rawGroups, setRawGroups] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [historyItems] = useState(readHistory());
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    async function loadData() {
      const cached = localStorage.getItem(CACHE_KEY);
      const now = Date.now();
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.updatedAt && now - parsed.updatedAt < CACHE_TTL_MS) {
            setRawGroups(parsed.rawGroups || []);
            setAllVideos(parsed.videos || []);
            return;
          }
        } catch {}
      }
      setLoading(true);
      try {
        const res = await API.get("video");
        const groups = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        const flat = [];
        for (const g of groups) {
          for (const v of g.videos || []) {
            flat.push({
              ...v,
              orderBy: Number.isFinite(v.orderBy) ? v.orderBy : undefined,
              Category: v.Category || { id: g.id, title: g.title, description: g.description, slug: g.slug, orderBy: g.orderBy }
            });
          }
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify({ rawGroups: groups, videos: flat, updatedAt: now }));
        setRawGroups(groups);
        setAllVideos(flat);
      } finally { setLoading(false); }
    }
    loadData();
  }, []);

  const historyVideos = useMemo(() => {
    if (!allVideos.length) return [];
    const bySlug = new Map(allVideos.map(v => [v.slug, v]));
    return [...historyItems]
        .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
        .map(h => bySlug.get(h.slug))
        .filter(Boolean);
  }, [allVideos, historyItems]);

  const categories = useMemo(() => {
    const arr = rawGroups
        .map(g => ({ id: g.id, title: (g.title || "").trim() || "Untitled", orderBy: typeof g.orderBy === "number" ? g.orderBy : -9999 }))
        .sort((a, b) => ((b.orderBy === 0 ? -Infinity : b.orderBy) - (a.orderBy === 0 ? -Infinity : a.orderBy)));

    return historyVideos.length ? [{ id: HISTORY_CATEGORY_ID, title: "Continue Watching", orderBy: Infinity }, ...arr] : arr;
  }, [rawGroups, historyVideos.length]);

  const filtered = useMemo(() => {
    let list = [...allVideos];
    if (qParam) {
      const q = qParam.toLowerCase();
      list = list.filter(v =>
          (v.title || "").toLowerCase().includes(q) ||
          (v.Category?.title || "").toLowerCase().includes(q) ||
          (v.description || "").toLowerCase().includes(q)
      );
    }
    if (categoryParam === HISTORY_CATEGORY_ID) list = [...historyVideos];
    else if (categoryParam) list = list.filter(v => String(v.Category?.id) === String(categoryParam));

    return list.sort(SORTERS[sortParam]);
  }, [allVideos, historyVideos, qParam, categoryParam, sortParam]);

  useEffect(() => { setVisibleCount(BATCH_SIZE); }, [qParam, categoryParam, sortParam, allVideos.length]);
  const visibleItems = filtered.slice(0, visibleCount);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(e => e[0]?.isIntersecting && setVisibleCount(c => Math.min(c + BATCH_SIZE, filtered.length)));
    obs.observe(el);
    return () => obs.disconnect();
  }, [filtered.length]);

  // Show auth modal after user scrolls a bit (logged out, once per session)
  useEffect(() => {
    if (auth?.user) return;
    const KEY = "AUTH_SCROLL_SHOWN";
    try {
      if (sessionStorage.getItem(KEY) === "1") return;
    } catch {}
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y > 800) {
        try { sessionStorage.setItem(KEY, "1"); } catch {}
        openAuthModal({ mode: "signin", closable: false });
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [auth?.user, openAuthModal]);
  const writeParam = useCallback((k, v) => {
    const next = new URLSearchParams(searchParams);
    v ? next.set(k, v) : next.delete(k);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  return (
      <>
        <Helmet>
          <title>Videos | MyGolfDNA.com</title>
        </Helmet>

        <Container className="py-2 py-lg-4">

          {/* Desktop: make sorting + categories the same as mobile */}
          <div className="videos-header d-none d-lg-flex align-items-center justify-content-between mt-2 mb-2">
            <div className="d-flex align-items-center gap-3">
              <InlineSortDropdown
                  value={sortParam}
                  onChange={handleSortChange}
                  className="sort-inline d-lg-inline"
              />
              <InlineCategoryDropdown
                  categories={categories}
                  value={categoryParam}
                  onChange={handleCategoryChange}
                  className="sort-inline d-lg-inline"
              />
            </div>
            <div className="text-muted small ms-auto">
              {isLoading ? "Loading…" : `${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {/* Mobile: Category (with ellipsis/left align) + Sort */}
          <div className="videos-header d-flex align-items-center justify-content-between mt-2 mb-2 d-lg-none">
            <div className="d-flex align-items-center gap-3">
              <InlineSortDropdown
                  value={sortParam}
                  onChange={handleSortChange}
                  className="sort-inline"
              />
              <InlineCategoryDropdown
                  categories={categories}
                  value={categoryParam}
                  onChange={handleCategoryChange}
                  className="sort-inline"
              />
            </div>

            <div className="text-muted small ms-auto">
              {isLoading ? "Loading…" : `${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
            </div>
          </div>

          <Row className="g-4 mt-1 video-grid-fade">
            {isLoading && Array.from({ length: 12 }).map((_, i) => (
                <Col key={i} xs={12} sm={6} lg={3} xl={3}>
                  <Card className="h-100">
                    <Placeholder as={Card.Img} animation="wave" style={{ aspectRatio: "16/9" }} />
                    <Card.Body>
                      <Placeholder as={Card.Title} animation="wave" xs={8} />
                      <Placeholder as="div" animation="wave" xs={5} />
                    </Card.Body>
                  </Card>
                </Col>
            ))}

            {!isLoading && visibleItems.length > 0 && visibleItems.map((v, i) => {
              const isLocked = !auth?.user?.planType && !v?.free;
              return (
                  <Col key={v.id} xs={12} sm={6} lg={3} xl={3} style={{ animationDelay: `${(i % 12) * 15}ms` }}>
                    <VideoCard video={v} isLocked={isLocked} disabledPreview={true} />
                  </Col>
              );
            })}

            {!isLoading && visibleItems.length === 0 && (
                <Col xs={12}>
                  <div className="text-center py-5 text-muted">
                    No videos found. Try adjusting filters.
                  </div>
                </Col>
            )}
          </Row>

          {!isLoading && filtered.length > visibleItems.length && (
              <div ref={sentinelRef} className="py-2 py-lg-4 text-center" style={{ minHeight: 1 }} aria-hidden>
                <Spinner animation="border" />
              </div>
          )}

        </Container>
      </>
  );
}
